/**
 * GoalFramePlacement — PMA-COCK-182/183. A goal frame drawn from the
 * shooter's own view (facing the goal): tap or keyboard-place where the ball
 * crossed it. Reused unchanged by the live post-shot panel
 * (`RefinementChipPanel`) and the review/feed inline editors
 * (`ReviewView`/`LiveEventFeed`) — only `source`/`value`/`onPlace` differ
 * per caller.
 *
 * PMA-COCK-183 renders the shared `GoalFrameScene` (real proportions, net
 * depth, grass, shaded woodwork) instead of the old flat rects, adds a cyan
 * zone highlight + name chip that follows the pointer (mouse hover or
 * touch-move), and draws the placed marker as a cyan disc with a halo
 * instead of an outcome-styled shape. The submitted payload
 * (`{x, y, zone, source}`) and its pointer/touch/keyboard mechanics are
 * unchanged from PMA-COCK-182.
 *
 * Coordinate mapping is fractional against this component's own rendered
 * rect, the same convention `TacticalField.tsx` uses for its pitch taps —
 * frame-local (x, y) never depends on pixel size, only on where inside the
 * scene's `SCENE.VIEWBOX_WIDTH x SCENE.VIEWBOX_HEIGHT` space (via
 * `svgToFrame`) the tap/hover/cursor lands. A pointer below the goal line
 * (the grass strip, `y < FRAME_PADDING.minY`) highlights nothing and a tap
 * there does nothing — it is not a valid zone.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { TFunction } from "i18next";
import GoalFrameScene from "./GoalFrameScene";
import {
  SCENE,
  FRAME_PADDING,
  POST_BAND,
  CROSSBAR_BAND,
  OVER_THRESHOLD,
  resolveGoalFrameZone,
  frameToSvg,
  svgToFrame,
  ZONE_I18N_KEYS,
  ZONE_FALLBACK_LABELS,
  SHOT_MARKER_COLOR,
  type GoalPlacement,
  type GoalPlacementSource,
  type GoalPlacementZone,
} from "../../utils/goalFrame";

const CURSOR_STEP = 0.1;
const TOUCH_DEDUPE_MS = 700;
/** Default cap on how wide the widget is allowed to grow (px) — the host
 * card can still be narrower; `width: 100%` inside that cap. Matches the
 * spec's "grows up to 560px wide". A caller-supplied `size` overrides this
 * cap (e.g. the review/feed inline editors' more compact rows). */
const DEFAULT_MAX_WIDTH = 560;

const POST_L_MIN = -POST_BAND;
const POST_L_MAX = POST_BAND;
const POST_R_MIN = 1 - POST_BAND;
const POST_R_MAX = 1 + POST_BAND;
const CROSSBAR_MIN = 1 - CROSSBAR_BAND;
const CROSSBAR_MAX = 1 + CROSSBAR_BAND;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Purely descriptive/accessible overlay regions — the real tap target is
 * the single interactive surface covering the whole canvas. Tiles exactly,
 * no gaps or overlaps, using the same thresholds `resolveGoalFrameZone`
 * evaluates against. Frame-local units (post-to-post = 1.0 on x,
 * ground-to-crossbar = 1.0 on y). */
const ZONE_RECTS: Record<
  GoalPlacementZone,
  { x: number; y: number; width: number; height: number }
> = {
  off_left: {
    x: FRAME_PADDING.minX,
    y: FRAME_PADDING.minY,
    width: POST_L_MIN - FRAME_PADDING.minX,
    height: FRAME_PADDING.maxY - FRAME_PADDING.minY,
  },
  off_right: {
    x: POST_R_MAX,
    y: FRAME_PADDING.minY,
    width: FRAME_PADDING.maxX - POST_R_MAX,
    height: FRAME_PADDING.maxY - FRAME_PADDING.minY,
  },
  over: {
    x: POST_L_MIN,
    y: OVER_THRESHOLD,
    width: POST_R_MAX - POST_L_MIN,
    height: FRAME_PADDING.maxY - OVER_THRESHOLD,
  },
  post_left: {
    x: POST_L_MIN,
    y: 0,
    width: POST_L_MAX - POST_L_MIN,
    height: CROSSBAR_MIN,
  },
  post_right: {
    x: POST_R_MIN,
    y: 0,
    width: POST_R_MAX - POST_R_MIN,
    height: CROSSBAR_MIN,
  },
  corner_left: {
    x: POST_L_MIN,
    y: CROSSBAR_MIN,
    width: POST_L_MAX - POST_L_MIN,
    height: CROSSBAR_MAX - CROSSBAR_MIN,
  },
  corner_right: {
    x: POST_R_MIN,
    y: CROSSBAR_MIN,
    width: POST_R_MAX - POST_R_MIN,
    height: CROSSBAR_MAX - CROSSBAR_MIN,
  },
  crossbar: {
    x: POST_L_MAX,
    y: CROSSBAR_MIN,
    width: POST_R_MIN - POST_L_MAX,
    height: CROSSBAR_MAX - CROSSBAR_MIN,
  },
  net: {
    x: POST_L_MAX,
    y: 0,
    width: POST_R_MIN - POST_L_MAX,
    height: CROSSBAR_MIN,
  },
};

/** Frame-local rect -> the scene's SVG pixel rect, via the two opposite
 * corners `frameToSvg` maps (its y-scale is negative, so the frame's
 * top-left corner in SVG space is (x, y + height)). */
const rectToSvg = (rect: {
  x: number;
  y: number;
  width: number;
  height: number;
}) => {
  const topLeft = frameToSvg(rect.x, rect.y + rect.height);
  const bottomRight = frameToSvg(rect.x + rect.width, rect.y);
  return {
    x: topLeft.px,
    y: topLeft.py,
    width: bottomRight.px - topLeft.px,
    height: bottomRight.py - topLeft.py,
  };
};

export interface GoalFramePlacementProps {
  value?: GoalPlacement | null;
  onPlace: (placement: GoalPlacement) => void;
  source: GoalPlacementSource;
  outcome?: string;
  disabled?: boolean;
  size?: number;
  "data-testid"?: string;
  t: TFunction<"logger">;
}

const GoalFramePlacement = ({
  value,
  onPlace,
  source,
  outcome,
  disabled = false,
  size,
  "data-testid": testIdProp,
  t,
}: GoalFramePlacementProps) => {
  const testId = testIdProp ?? "goal-frame-placement";
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastTouchPlacementAt = useRef(0);
  const [cursor, setCursor] = useState<{ x: number; y: number }>(() =>
    value ? { x: value.x, y: value.y } : { x: 0.5, y: 0.5 },
  );
  const [hoveredZone, setHoveredZone] = useState<GoalPlacementZone | null>(
    null,
  );

  const place = useCallback(
    (x: number, y: number) => {
      if (disabled) return;
      const clampedX = clamp(x, FRAME_PADDING.minX, FRAME_PADDING.maxX);
      const clampedY = clamp(y, FRAME_PADDING.minY, FRAME_PADDING.maxY);
      const zone = resolveGoalFrameZone(clampedX, clampedY);
      setCursor({ x: clampedX, y: clampedY });
      onPlace({ x: clampedX, y: clampedY, zone, source });
    },
    [disabled, onPlace, source],
  );

  /** Client point -> frame-local (x, y), or `null` when the point falls in
   * the grass strip below the goal line (`y < FRAME_PADDING.minY`) — that
   * band is decorative, not a valid zone. */
  const frameAtClientPoint = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const fracX = (clientX - rect.left) / rect.width;
      const fracY = (clientY - rect.top) / rect.height;
      const { x, y } = svgToFrame(
        fracX * SCENE.VIEWBOX_WIDTH,
        fracY * SCENE.VIEWBOX_HEIGHT,
      );
      if (y < FRAME_PADDING.minY) return null;
      return { x, y };
    },
    [],
  );

  const placeAtClientPoint = useCallback(
    (clientX: number, clientY: number) => {
      const frame = frameAtClientPoint(clientX, clientY);
      if (!frame) return;
      place(frame.x, frame.y);
    },
    [frameAtClientPoint, place],
  );

  // Touch single-submission — mirrors TacticalField.tsx's PMA-COCK-178
  // pattern: a touch pointerup places and stamps a timestamp; the synthetic
  // click a touch tap ALSO emits is then suppressed for 700ms so one tap
  // never places twice.
  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled || event.pointerType !== "touch") return;
      lastTouchPlacementAt.current = Date.now();
      placeAtClientPoint(event.clientX, event.clientY);
    },
    [disabled, placeAtClientPoint],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (Date.now() - lastTouchPlacementAt.current < TOUCH_DEDUPE_MS) return;
      placeAtClientPoint(event.clientX, event.clientY);
    },
    [disabled, placeAtClientPoint],
  );

  // PMA-COCK-183: cyan zone highlight + name chip, following the pointer on
  // both mouse hover and touch-move. Cleared on pointerleave; a point in
  // the grass strip clears it too (no zone there).
  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) {
        setHoveredZone(null);
        return;
      }
      const frame = frameAtClientPoint(event.clientX, event.clientY);
      if (!frame) {
        setHoveredZone(null);
        return;
      }
      setHoveredZone(resolveGoalFrameZone(frame.x, frame.y));
    },
    [disabled, frameAtClientPoint],
  );

  const handlePointerLeave = useCallback(() => {
    setHoveredZone(null);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          setCursor((prev) => ({
            ...prev,
            x: clamp(
              prev.x - CURSOR_STEP,
              FRAME_PADDING.minX,
              FRAME_PADDING.maxX,
            ),
          }));
          break;
        case "ArrowRight":
          event.preventDefault();
          setCursor((prev) => ({
            ...prev,
            x: clamp(
              prev.x + CURSOR_STEP,
              FRAME_PADDING.minX,
              FRAME_PADDING.maxX,
            ),
          }));
          break;
        case "ArrowUp":
          event.preventDefault();
          setCursor((prev) => ({
            ...prev,
            y: clamp(
              prev.y + CURSOR_STEP,
              FRAME_PADDING.minY,
              FRAME_PADDING.maxY,
            ),
          }));
          break;
        case "ArrowDown":
          event.preventDefault();
          setCursor((prev) => ({
            ...prev,
            y: clamp(
              prev.y - CURSOR_STEP,
              FRAME_PADDING.minY,
              FRAME_PADDING.maxY,
            ),
          }));
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          place(cursor.x, cursor.y);
          break;
        default:
          break;
      }
    },
    [disabled, cursor, place],
  );

  const marker = value ? frameToSvg(value.x, value.y) : null;
  const cursorPoint = frameToSvg(cursor.x, cursor.y);
  const highlightRect = hoveredZone ? rectToSvg(ZONE_RECTS[hoveredZone]) : null;
  const zoneLabel = (zone: GoalPlacementZone) =>
    t(`goalFrame.zones.${ZONE_I18N_KEYS[zone]}`, ZONE_FALLBACK_LABELS[zone]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      role="button"
      aria-label={t("goalFrame.instruction", "Tap where the ball went")}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onPointerUp={handlePointerUp}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      onKeyDown={handleKeyDown}
      style={{
        width: "100%",
        maxWidth: size ?? DEFAULT_MAX_WIDTH,
        position: "relative",
        touchAction: "manipulation",
        cursor: disabled ? "default" : "pointer",
      }}
      className="select-none outline-none focus-visible:ring-2 focus-visible:ring-teal-400 rounded"
    >
      <GoalFrameScene
        instanceId={testId}
        ariaLabel={t("goalFrame.instruction", "Tap where the ball went")}
        role="presentation"
        t={t}
      >
        {/* Descriptive, non-interactive zone overlays for accessibility. */}
        {(Object.keys(ZONE_RECTS) as GoalPlacementZone[]).map((zone) => {
          const r = rectToSvg(ZONE_RECTS[zone]);
          return (
            <rect
              key={zone}
              data-testid={`goal-frame-zone-${zone}`}
              aria-label={zoneLabel(zone)}
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              fill="transparent"
              pointerEvents="none"
            />
          );
        })}

        {/* PMA-COCK-183: cyan zone highlight under the pointer */}
        {highlightRect && (
          <rect
            data-testid="goal-frame-zone-highlight"
            x={highlightRect.x}
            y={highlightRect.y}
            width={highlightRect.width}
            height={highlightRect.height}
            fill="#22d3ee"
            opacity={0.28}
            rx={4}
            pointerEvents="none"
          />
        )}

        {/* Keyboard cursor */}
        <circle
          data-testid={`${testId}-cursor`}
          cx={cursorPoint.px}
          cy={cursorPoint.py}
          r={5}
          fill="none"
          stroke="#5eead4"
          strokeDasharray="3 2"
          strokeWidth={1.5}
        />

        {/* Placed marker — PMA-COCK-183: always a cyan disc with a halo,
            independent of outcome. */}
        {value && marker && (
          <g
            data-testid={`${testId}-marker`}
            data-zone={value.zone}
            data-outcome={outcome ?? undefined}
          >
            <circle
              cx={marker.px}
              cy={marker.py}
              r={20}
              fill="#22d3ee"
              opacity={0.25}
            />
            <circle
              cx={marker.px}
              cy={marker.py}
              r={11}
              fill="#22d3ee"
              stroke="#ffffff"
              strokeWidth={3}
            />
          </g>
        )}
      </GoalFrameScene>

      {/* PMA-COCK-183: zone-name chip, an HTML overlay (not SVG text) so it
          renders crisply at any scale. */}
      {hoveredZone && (
        <div
          data-testid="goal-frame-zone-name"
          className="pointer-events-none absolute left-2 top-2 rounded bg-cyan-400 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-900"
        >
          {zoneLabel(hoveredZone)}
        </div>
      )}
    </div>
  );
};

export default GoalFramePlacement;

// ---------------------------------------------------------------------------
// WoodworkTargetSelector — client wave 2026-09-19, "Tiro al palo"
// ---------------------------------------------------------------------------

/** The three woodwork surfaces as tap targets — design direction: "the
 * existing goal frame reduced to its three woodwork targets", explicitly
 * NOT a new modal. Reuses `GoalFrameScene` and the same `ZONE_RECTS`/
 * `rectToSvg` geometry `GoalFramePlacement`'s own zone overlays already use
 * above, restricted to the three woodwork zones, filled the map's own
 * fixed woodwork amber (`SHOT_MARKER_COLOR.woodwork`, `#fbbf24`) so the
 * button (`QUICK_ACTION_COLORS.Woodwork`, the same Tailwind `amber-400`),
 * this selector, and the resulting `goal-mouth-marker-woodwork` chart mark
 * are one colour end to end. Renders as an ordinary panel where
 * `OutcomeSelectionPanel` normally sits (`ActionStage.tsx`), not a pitch
 * overlay — the operator has already picked the player, this is the only
 * remaining choice before dispatch. */
const WOODWORK_TARGETS: readonly ["post_left", "crossbar", "post_right"] = [
  "post_left",
  "crossbar",
  "post_right",
];

/**
 * T10 (EPIC-61 brief 15/AC14, client wave 2026-09-19, contrast audit fix).
 * The target rects previously used a single element-level `opacity={0.55}`,
 * which ties the fill AND the stroke to the same 55% wash — the boundary
 * meant to read as a "tap target here" outline was exactly as washed out as
 * the translucent fill itself. Computed against the goal-frame scene's own
 * light post/crossbar shading (`GoalFrameScene.tsx`, roughly #aab6c8 to
 * #f5f7fa): the combined fill+stroke composited to 1.06-1.29:1, well under
 * the 3:1 SC 1.4.11 non-text-UI-component floor these are tap targets on a
 * tablet, so real, not theoretical.
 *
 * Fix: split `fillOpacity`/`strokeOpacity` apart instead of one shared
 * `opacity`. The fill KEEPS the translucent amber wash (identity, same
 * `SHOT_MARKER_COLOR.woodwork` the Woodwork button and the shot-map chart
 * mark already use — deliberately not changed, per the lead's explicit
 * "do not solve it by changing the amber"). The stroke gets its own colour,
 * `amber-900` (`#78350f`, same hue family — still reads as "amber", just
 * the dark end of it) at full opacity: 4.42-8.45:1 against the same three
 * post-shading colours, comfortably clearing the floor. `strokeWidth` bumped
 * 2 -> 3 so the now-solid boundary reads clearly at arm's length, not as a
 * hairline.
 */
const WOODWORK_TARGET_STROKE_COLOR = "#78350f"; // amber-900

export interface WoodworkTargetSelectorProps {
  onSelect: (zone: "post_left" | "crossbar" | "post_right") => void;
  onCancel: () => void;
  disabled?: boolean;
  size?: number;
  t: TFunction<"logger">;
}

export const WoodworkTargetSelector = ({
  onSelect,
  onCancel,
  disabled = false,
  size,
  t,
}: WoodworkTargetSelectorProps) => {
  const testId = "woodwork-target-selector";
  const zoneLabel = (zone: GoalPlacementZone) =>
    t(`goalFrame.zones.${ZONE_I18N_KEYS[zone]}`, ZONE_FALLBACK_LABELS[zone]);

  const handleKeyDown = (
    event: React.KeyboardEvent<SVGRectElement>,
    zone: "post_left" | "crossbar" | "post_right",
  ) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(zone);
    }
  };

  return (
    <div
      data-testid={testId}
      className="rounded-lg border border-slate-700 bg-slate-900/80 p-3 space-y-2"
      style={{ width: "100%", maxWidth: size ?? DEFAULT_MAX_WIDTH }}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
        {t(
          "goalFrame.woodworkInstruction",
          "Tap which part of the frame was struck",
        )}
      </p>
      <GoalFrameScene
        instanceId={testId}
        ariaLabel={t("goalMouthMap.legendWoodwork", "Woodwork")}
        role="presentation"
        t={t}
      >
        {WOODWORK_TARGETS.map((zone) => {
          const r = rectToSvg(ZONE_RECTS[zone]);
          return (
            <rect
              key={zone}
              data-testid={`woodwork-target-${zone}`}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={zoneLabel(zone)}
              aria-disabled={disabled || undefined}
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              fill={SHOT_MARKER_COLOR.woodwork}
              fillOpacity={0.55}
              stroke={WOODWORK_TARGET_STROKE_COLOR}
              strokeOpacity={1}
              strokeWidth={3}
              style={{
                cursor: disabled ? "default" : "pointer",
                outline: "none",
              }}
              onClick={() => !disabled && onSelect(zone)}
              onKeyDown={(event) => handleKeyDown(event, zone)}
            />
          );
        })}
      </GoalFrameScene>
      <button
        type="button"
        data-testid="woodwork-target-cancel"
        onClick={onCancel}
        disabled={disabled}
        className="w-full px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold disabled:opacity-50"
      >
        {t("quickActionCancel", "Cancel")}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// WoodworkTargetOverlay — EPIC-96 (AC2, client wave 2026-09-24)
// ---------------------------------------------------------------------------

// PMA-COCK-178 pattern (also used by GoalPlacementOverlay.tsx and
// TacticalField.tsx): a touch tap fires both `pointerup` and a trailing
// synthetic `click` — the scrim's own click handler must ignore the one
// immediately following a touch dismiss so a single tap never double-fires.
const WOODWORK_OVERLAY_TOUCH_DEDUPE_MS = 700;

/**
 * EPIC-96 (AC2, client wave 2026-09-24): "Los 'tiro al palo' sale abajo
 * todavia, hay que colocarlo como los otros que sale en la cancha" — the
 * woodwork target selector used to render in-flow BELOW the pitch
 * (`ActionStage.tsx`, in place of `OutcomeSelectionPanel`). This wraps the
 * unchanged `WoodworkTargetSelector` in the SAME scrim + centred-card
 * pattern `GoalPlacementOverlay.tsx` already established for the
 * goal-placement modal, so both pickers now open as a modal OVER the pitch:
 * pitch stays visible behind the scrim, `ActionStage.tsx` mounts this
 * through `PlayerSelectorPanel`'s `fieldOverlay` slot (included in
 * `overlayBlocksPitch`, same as the goal-placement overlay), and the scrim
 * tap / Escape both cancel exactly like the goal-placement overlay's own
 * Skip. `WoodworkTargetSelector` itself, and its own `woodwork-target-*`
 * testids, are reused byte-for-byte — this is purely a wrapper, no new
 * eligibility rule and no change to the dispatched event.
 */
export interface WoodworkTargetOverlayProps {
  onSelect: (zone: "post_left" | "crossbar" | "post_right") => void;
  onCancel: () => void;
  disabled?: boolean;
  t: TFunction<"logger">;
}

export const WoodworkTargetOverlay = ({
  onSelect,
  onCancel,
  disabled = false,
  t,
}: WoodworkTargetOverlayProps) => {
  const lastScrimTouchDismissAt = useRef(0);

  // Escape closes regardless of which element inside the overlay currently
  // holds focus — same behaviour as GoalPlacementOverlay.tsx's own handler.
  useEffect(() => {
    const handleKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onCancel();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  const handleScrimPointerUp = useCallback(
    (pointerEvent: React.PointerEvent<HTMLDivElement>) => {
      if (pointerEvent.pointerType !== "touch") return;
      lastScrimTouchDismissAt.current = Date.now();
      onCancel();
    },
    [onCancel],
  );

  const handleScrimClick = useCallback(() => {
    if (
      Date.now() - lastScrimTouchDismissAt.current <
      WOODWORK_OVERLAY_TOUCH_DEDUPE_MS
    )
      return;
    onCancel();
  }, [onCancel]);

  return (
    <div className="absolute inset-0">
      <div
        data-testid="woodwork-target-scrim"
        className="absolute inset-0 bg-slate-950/70"
        onClick={handleScrimClick}
        onPointerUp={handleScrimPointerUp}
      />
      <div className="absolute inset-0 flex items-center justify-center p-3 pointer-events-none">
        <div
          data-testid="woodwork-target-overlay"
          role="dialog"
          aria-modal="false"
          aria-label={t(
            "goalFrame.woodworkInstruction",
            "Tap which part of the frame was struck",
          )}
          className="pointer-events-auto w-full"
          style={{ width: "min(92%, 560px)" }}
        >
          <WoodworkTargetSelector
            onSelect={onSelect}
            onCancel={onCancel}
            disabled={disabled}
            t={t}
          />
        </div>
      </div>
    </div>
  );
};
